import * as L from "lonna"
import { Board, Color, Item, Point, drawingPath, newDrawing } from "../../../common/src/domain"
import { Dispatch } from "../store/board-store"
import { BoardCoordinateHelper } from "./board-coordinates"
import { BoardFocus, noFocus } from "./board-focus"
import { installKeyboardShortcut, plainKey } from "./keyboard-shortcuts"
import { ToolController } from "./tool-selection"
import { isDark } from "../theme"

const MIN_STEP = 0.15 // em between recorded points
export const DEFAULT_STROKE_WIDTH = 0.3 // em

// Freehand pen tool. Pointer drag on the board records points; on release a "drawing" item is added.
export function itemDrawHandler(
    boardElem: L.Property<HTMLElement | null>,
    board: L.Property<Board>,
    coordinateHelper: BoardCoordinateHelper,
    toolController: ToolController,
    focus: L.Atom<BoardFocus>,
    dispatch: Dispatch,
) {
    const livePoints = L.atom<Point[]>([])
    const penColor = (): Color => localStorage.penColor || (isDark.get() ? "#e8e8ea" : "#00263a")

    installKeyboardShortcut(plainKey("p"), () => toolController.tool.set("draw"))

    boardElem.forEach((el) => {
        if (!el) return
        el.addEventListener("pointerdown", (e) => {
            if (toolController.tool.get() !== "draw" || e.button !== 0) return
            e.preventDefault()
            e.stopPropagation()
            focus.set(noFocus)
            let points: Point[] = [coordinateHelper.pageToBoardCoordinates({ x: e.pageX, y: e.pageY })]
            livePoints.set(points)
            const move = (m: PointerEvent) => {
                const p = coordinateHelper.pageToBoardCoordinates({ x: m.pageX, y: m.pageY })
                const last = points[points.length - 1]
                if (Math.hypot(p.x - last.x, p.y - last.y) >= MIN_STEP) {
                    points = [...points, p]
                    livePoints.set(points)
                }
            }
            const up = () => {
                document.removeEventListener("pointermove", move)
                document.removeEventListener("pointerup", up)
                document.removeEventListener("pointercancel", up)
                livePoints.set([])
                if (points.length < 2) return
                const item = newDrawing(points, penColor(), DEFAULT_STROKE_WIDTH)
                dispatch({ action: "item.add", boardId: board.get().id, items: [item], connections: [] })
            }
            document.addEventListener("pointermove", move)
            document.addEventListener("pointerup", up)
            document.addEventListener("pointercancel", up)
        })
    })

    // Live preview of the stroke being drawn (board coordinates, em)
    const livePath = L.view(livePoints, drawingPath)
    return { livePath, penColor, strokeWidth: DEFAULT_STROKE_WIDTH }
}

export const rememberPenColor = (item: Item) => {
    if (item.type === "drawing") localStorage.penColor = item.color
}
